import {
  useEffect,
  useCallback,
  useState,
  useDeferredValue,
} from 'react';
import SendMessageContainer from './SendMessageContainer';
import { Box, Typography, Avatar } from '@mui/material';
import { useChatStore } from 'store/useChatStore';
import { useSearchParams } from 'wouter';
import Messages from 'components/Chat/ChatMessages';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelectedDeviceStore } from 'store/useSelectedDeviceStore';
import showSnackbar from 'utils/showSnackbar';
import Loading from 'components/Loading';
import { useMixpanel } from 'utils/hooks/useMixPanel';
import SuggestFinishAlert from './SuggestFinish';
import { useConversationPatch } from 'requests/tanstack/useConversation';
import { useConversationMessages } from 'requests/tanstack/useConversationMessages';
import { ConversationPriorityEnum, MessageStatusEnum } from 'common/enums';
import ChangeConversationPriorityMenu from './ChangeConversationPriorityMenu';
import { uniqBy, uniqueId } from 'lodash';
import { DEFAULT_HEIGTH_TO_NAVBAR_AND_CHAT_HEADER } from 'utils/constants';
import { useShallow } from 'zustand/shallow';
import { useAlyviaUserStore } from 'store/useAlyviaUseStore';

import ScheduledMessagesDrawer from './ScheduledMessagesDrawer';
import { InfoBanner } from 'components';

import * as S from './styles';
import { messageSchedulerHttpGateway } from 'infra/gateways';
import { ScheduledMessage } from 'domain/gateways';
import { canModifyScheduledMessage } from 'utils/canModifyScheduledMessage';
import useSendMessageContainerLogic from './SendMessageContainer/useSendMessageContainerLogic';
import ConversationSumarizerMenu from './ConversationSummarizerMenu';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';

export default function Chat() {
  const [editingScheduledMessage, setEditingScheduledMessage] = useState<{id: string, newSendAt: string | Date, message: string} | null>(null);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedDevice } = useSelectedDeviceStore();
  const { alyviaUser } = useAlyviaUserStore();

  const {
    selectedConversation,
    closeConversation,
    setOpenedChatMessages,
    openedChatMessages,
  } = useChatStore(
    useShallow((state) => ({
      selectedConversation: state.selectedConversation,
      openedChatMessages: state.openedChatMessages,
      setOpenedChatMessages: state.setOpenedChatMessages,
      closeConversation: state.closeConversation,
    }))
  );
  const {
    data: selectedConversationMessages,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationMessages(selectedDevice, selectedConversation.id);

  const deferredMessages = useDeferredValue(openedChatMessages);

  const {
      requestSendTextMessage,
    } = useSendMessageContainerLogic({ currentMessages: deferredMessages });

  const { mutateAsync: handleConversationMutation } = useConversationPatch();

  const changeConversationPriority = (
    newPriority: ConversationPriorityEnum
  ) => {
    handleConversationMutation({
      conversationId: selectedConversation?.id,
      conversationPriority: newPriority,
    })
      .then(() => {
        showSnackbar('Prioridade alterada com sucesso!', 'success');
      })
      .catch((error) =>
        showSnackbar(
          `Falha ao alterar prioridade! ERR:${error.message}`,
          'error'
        )
      );
  };

  const handleCloseConversation = useCallback(() => {
    setSearchParams({
      deviceId: searchParams.get('deviceId'),
      conversationId: undefined,
    });
    return closeConversation();
  }, [searchParams, closeConversation, setSearchParams]);

  const { chatInteraction } = useMixpanel();

  const userAvatar = selectedConversation?.messages[0]?.contact?.imgData
    ? `data:image/;base64,${selectedConversation?.messages[0]?.contact?.imgData}`
    : undefined;

  const userName = 
    selectedConversation?.messages[0]?.contact?.name
    || selectedConversation?.name;

  const hasScheduledMessages = scheduledMessages.length > 0;

  if (error) {
    showSnackbar("Falha ao buscar mensagens!", "error");
    console.error(error);
  }

  useEffect(() => {
    const getScheduledMessages = async () => {
      const scheduledMessages = await messageSchedulerHttpGateway.getAll(
        selectedDevice,
        alyviaUser?.email,
        selectedConversation?.id
      );
      setScheduledMessages(scheduledMessages)
    };

    try {
      getScheduledMessages();
    } catch (error) {
      showSnackbar("Falha ao buscar mensagens agendadas!", "error");
    }
  }, [selectedDevice, alyviaUser?.email]);

  useEffect(() => {
    if (isFetchingNextPage || isLoading) setMessagesLoading(true);
    if (!isFetchingNextPage && !isLoading)
      setTimeout(() => setMessagesLoading(false), 500);
  }, [isLoading, isFetchingNextPage]);

  useEffect(() => {
    const messages =
      uniqBy(
        selectedConversationMessages?.pages?.flat(),
        (message) => message.id
      ) || [];
    setOpenedChatMessages(messages);
  }, [selectedConversationMessages?.pages, setOpenedChatMessages]);

  useEffect(() => {
    const handleEscPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseConversation();
      }
    };
    document.addEventListener('keydown', handleEscPress);
    return () => {
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [handleCloseConversation]);

  useEffect(() => {
    if (selectedDevice && selectedConversation?.id) {
      chatInteraction(selectedDevice, 'thread', {
        conversationId: selectedConversation.id,
      });
    }
  }, [chatInteraction, selectedConversation.id, selectedDevice]);

  const onDeleteScheduledMessage = useCallback(
    async (id: string, sendAt: string | Date) => {
      const { success, error } = canModifyScheduledMessage(sendAt);
      if (!success) {
        showSnackbar(error, 'error');
        return;
      }
      await messageSchedulerHttpGateway.delete(id);
      setScheduledMessages((prev) => prev.filter((msg) => msg.id !== id));
      showSnackbar('Mensagem Excluída com sucesso!', 'success');
    }, []
  );

  const onRescheduleScheduledMessage = useCallback(
    async (props: {id: string, newSendAt: string | Date, message: string}) => {
      try {
        await messageSchedulerHttpGateway.update(props.id, props.newSendAt, props.message);
        setScheduledMessages((prev) => prev.map(
          (msg) => msg.id === props.id ? {...msg, sendAt: props.newSendAt} : msg
        ));
        showSnackbar('Mensagem Reagendada com sucesso!', 'success');
      } catch (error) {
        showSnackbar('Falha ao reagendar mensagem!', 'error');
      }
    }, []
  );

  const onEditScheduledMessageEnd = useCallback(
    async (props: {id: string, newSendAt: string | Date, message: string}) => {
      try {
        await messageSchedulerHttpGateway.update(props.id, props.newSendAt, props.message);
        setScheduledMessages((prev) => prev.map(
          (msg) => msg.id === props.id ? {...msg, message: props.message} : msg
        ));
        showSnackbar('Mensagem Editada com sucesso!', 'success');
      } catch (error) {
        showSnackbar('Falha ao editar mensagem!', 'error');
      }
      setEditingScheduledMessage(null);
    }, []
  );

  const onScheduleMessage = useCallback(
    async (props: {sendAt: string | Date, message: string, id?: string}) => {
      let response;
      let messageId: string;
      try {
        response = await messageSchedulerHttpGateway.schedule({
          id: props.id,
          message: props.message,
          sendAt: props.sendAt,
          deviceId: selectedDevice,
          jid: selectedConversation?.messages[0]?.sender,
          conversationId: selectedConversation?.id,
          status: MessageStatusEnum.PENDING,
          userId: alyviaUser?.email,
        })
        messageId = response?.id || uniqueId();
        setScheduledMessages((prev) => [...prev, {id: messageId, sendAt: props.sendAt, message: props.message, deviceId: selectedDevice, conversationId: selectedConversation?.id, userId: alyviaUser?.email, status: MessageStatusEnum.PENDING, jid: selectedConversation?.messages[0]?.sender}]);
        showSnackbar('Mensagem Agendada com sucesso!', 'success');
      } catch (error) {
        setScheduledMessages((prev) => [...prev, {id: messageId, sendAt: props.sendAt, message: props.message, deviceId: selectedDevice, conversationId: selectedConversation?.id, userId: alyviaUser?.email, status: MessageStatusEnum.PENDING, jid: selectedConversation?.messages[0]?.sender, isError: true, errorStatus: error.status}]);
        showSnackbar('Falha ao agendar mensagem!', 'error');
      }
    }, []
  );

  const onRetryScheduledMessage = useCallback(
    async ({id, sendAt, message}: {id: string, sendAt: string | Date, message: string}) => {
      try {
        await messageSchedulerHttpGateway.schedule({
          id,
          message,
          sendAt,
          deviceId: selectedDevice,
          jid: selectedConversation?.messages[0]?.sender,
          conversationId: selectedConversation?.id,
          status: MessageStatusEnum.PENDING,
          userId: alyviaUser?.email,
        });
        setScheduledMessages((prev) => prev.filter((msg) => msg.id !== id));
        showSnackbar('Mensagem Reagendada com sucesso!', 'success');
      } catch (error) {
        showSnackbar('Falha ao reagendar mensagem!', 'error');
      }
    }, []
  );

  const onSendScheduledMessage = useCallback(
    async ({id, sendAt, message}: {id: string, sendAt: string | Date, message: string}) => {
      try {
        await messageSchedulerHttpGateway.delete(id);
        setScheduledMessages((prev) => prev.filter((msg) => msg.id !== id));
        requestSendTextMessage(message, () => setEditingScheduledMessage(null));
        showSnackbar('Mensagem Enviada com sucesso!', 'success');
      } catch (error) {
        showSnackbar('Falha ao enviar mensagem!', 'error');
      }
    }, [requestSendTextMessage, setEditingScheduledMessage]);

  const onEditScheduledMessageStart = useCallback(
    async (props: {id: string, newSendAt: string | Date, message: string}) => {
      setEditingScheduledMessage(props);
    }, []
  );

  const hasErrorMessage = scheduledMessages.some((msg) => msg.isError);

  return (
    <Box display="flex" width="100%" height="100%">
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        position="relative"
        overflow="hidden"
      >
        <Box
          boxShadow={1}
          bgcolor="white"
          width="100%"
          height={`${DEFAULT_HEIGTH_TO_NAVBAR_AND_CHAT_HEADER}px`}
          zIndex={1001}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            p={2}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <ArrowBackIcon
                sx={{ display: { md: "none" } }}
                onClick={handleCloseConversation}
              />
              <Avatar
                src={userAvatar}
              />
              <Box flex={1} maxWidth="350px">
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {userName}
                </Typography>
              </Box>
            </Box>
            <Box display="flex">
              <ConversationSumarizerMenu
                conversationId={selectedConversation?.id}
                deviceId={selectedDevice}
              />
              <ChangeConversationPriorityMenu
                conversationPriority={selectedConversation?.priority}
                onChangeConversationPriority={changeConversationPriority}
              />
            </Box>
          </Box>
        </Box>
  
        {messagesLoading && <Loading />}
  
        {(selectedConversation?.priority === ConversationPriorityEnum.HIGH ||
          selectedConversation?.priority === ConversationPriorityEnum.MEDIUM) &&
          deferredMessages?.some((msg) => msg?.shouldSuggestFinish === true) && (
            <SuggestFinishAlert
              selectedConversationId={selectedConversation.id}
              onConversationFinish={handleConversationMutation}
            />
          )}
  
        <S.ChatContainer>
          {deferredMessages && (
            <Messages
              messages={deferredMessages}
              messagesLoading={isLoading}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          )}
        </S.ChatContainer>

        <InfoBanner
          open={!isDrawerOpen && hasScheduledMessages}
          isError={hasErrorMessage}
          icon={<CalendarIcon />}
          content={
            <>
              {hasErrorMessage ? (
                <Typography variant="body2" component="span">
                  Existem mensagens agendadas que apresentaram erro.
                  <Box
                    component="span"
                    sx={{ color: '#007AFF', cursor: 'pointer', ml: 0.5, fontWeight: 500 }}
                    onClick={() => setIsDrawerOpen(true)}
                  >
                    Clique aqui para visualizar.
                  </Box>
                </Typography>
                ) : (
                <Typography variant="body2" component="span">
                  {scheduledMessages.length} mensagem{scheduledMessages.length > 1 ? 's' : ''} agendada{scheduledMessages.length > 1 ? 's' : ''} para ser enviada nesta conversa.{' '}
                  <Box
                    component="span"
                    sx={{ color: '#007AFF', cursor: 'pointer', ml: 0.5, fontWeight: 500 }}
                    onClick={() => setIsDrawerOpen(true)}
                  >
                    Ver mensagens agendadas.
                  </Box>
                </Typography>
                )}
            </>
          }
        />
        <SendMessageContainer
          sendScheduledMessage={onSendScheduledMessage}
          onScheduleMessage={onScheduleMessage}
          editingScheduledMessage={editingScheduledMessage}
          onEditScheduledMessageEnd={onEditScheduledMessageEnd}
          setEditingScheduledMessage={setEditingScheduledMessage}
          currentMessages={deferredMessages}
        />
      </Box>
  
      {isDrawerOpen && hasScheduledMessages && (
        <ScheduledMessagesDrawer
          onEditScheduledMessage={onEditScheduledMessageStart}
          onDeleteScheduledMessage={onDeleteScheduledMessage}
          onRescheduleScheduledMessage={onRescheduleScheduledMessage}
          onRetryScheduledMessage={onRetryScheduledMessage}
          onSendScheduledMessage={onSendScheduledMessage}
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          messages={scheduledMessages.map((sMessage) => ({
            isError: sMessage.isError,
            errorStatus: sMessage.errorStatus,
            id: sMessage.id,
            preview: sMessage.message,
            sendAt: sMessage.sendAt,
            name: userName,
            avatar: userAvatar,
            status: sMessage.status,
          }))}
        />
      )}
    </Box>
  );
  
}
